/**
 * The automatic backup (decision 0017): the section of File → Settings, and the hook that
 * writes the copies.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Translate } from '@elec/i18n';
import type { Platform } from '@elec/platform';
import { backUp, backupSettings, saveBackupSettings, type BackupSettings } from './autoBackup.ts';

type Project = { id: string; meta: { title: string } };

export interface AutoBackup {
  last: { at: Date; name: string } | null;
  error: string | null;
  /** Write a copy now (also from the Settings button). */
  now(): Promise<void>;
}

/**
 * Every minute: when backups are on, the folder can be written, the project changed since the
 * last copy and the interval has passed, write a copy.
 */
export function useAutoBackup(platform: Platform, project: Project, bytes: () => Uint8Array): AutoBackup {
  const [last, setLast] = useState<AutoBackup['last']>(null);
  const [error, setError] = useState<string | null>(null);
  const state = useRef({ project, bytes, saved: null as Project | null, at: 0, busy: false });
  state.current.project = project;
  state.current.bytes = bytes;
  const write = useCallback(async () => {
    const folder = platform.backups;
    const s = state.current;
    if (!folder || s.busy) return;
    s.busy = true;
    try {
      const p = s.project;
      const at = new Date();
      const name = await backUp(folder, p, s.bytes(), backupSettings(platform).keep, at);
      s.saved = p;
      s.at = at.getTime();
      setLast({ at, name });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      s.busy = false;
    }
  }, [platform]);
  useEffect(() => {
    const folder = platform.backups;
    if (!folder) return;
    // The first copy waits one interval: opening a project is not a change.
    state.current.saved = state.current.project;
    state.current.at = Date.now();
    const tick = async () => {
      const cfg = backupSettings(platform);
      const s = state.current;
      if (!cfg.enabled || s.project === s.saved) return;
      if (Date.now() - s.at < cfg.minutes * 60_000) return;
      if ((await folder.status()) !== 'ok') return;
      await write();
    };
    const id = setInterval(() => void tick(), 60_000);
    return () => clearInterval(id);
  }, [platform, write]);
  return { last, error, now: write };
}

export function BackupSection(props: {
  t: Translate;
  platform: Platform;
  backup: AutoBackup;
  locale: string;
}) {
  const { t, platform, backup } = props;
  const folder = platform.backups;
  const [cfg, setCfg] = useState<BackupSettings>(() => backupSettings(platform));
  const [where, setWhere] = useState<string | null>(null);
  const [status, setStatus] = useState<'ok' | 'needs-permission' | 'none'>('none');
  const refresh = useCallback(async () => {
    if (!folder) return;
    setWhere(await folder.describe());
    setStatus(await folder.status());
  }, [folder]);
  useEffect(() => void refresh(), [refresh]);
  if (!folder) return null;
  const update = (s: BackupSettings) => {
    saveBackupSettings(platform, s);
    setCfg(s);
  };
  const choose = async () => {
    const name = await folder.choose();
    if (name) update({ ...cfg, enabled: true });
    await refresh();
  };
  return (
    <fieldset className="settings-backup" data-testid="settings-backup">
      <legend>{t('backup.title')}</legend>
      <label className="check">
        <input
          type="checkbox"
          checked={cfg.enabled}
          onChange={(e) => {
            if (e.target.checked && status === 'none') void choose();
            else update({ ...cfg, enabled: e.target.checked });
          }}
          data-testid="backup-enabled"
        />
        <span>{t('backup.enable')}</span>
      </label>
      <p className="muted small">{t('backup.hint')}</p>
      <p data-testid="backup-folder">
        {t('backup.folder')}: <strong>{where ?? t('backup.noFolder')}</strong>{' '}
        <button onClick={() => void choose()} data-testid="backup-choose">
          {t('backup.choose')}
        </button>
      </p>
      {status === 'needs-permission' && (
        <p className="warn">
          {t('backup.needsPermission')}{' '}
          <button onClick={() => void folder.resume().then(refresh)} data-testid="backup-resume">
            {t('backup.resume')}
          </button>
        </p>
      )}
      <div className="row">
        <label>
          {t('backup.every')}{' '}
          <select
            value={cfg.minutes}
            onChange={(e) => update({ ...cfg, minutes: Number(e.target.value) })}
            data-testid="backup-minutes"
          >
            {[10, 30, 60, 240].map((m) => (
              <option key={m} value={m}>
                {m < 60 ? `${m} min` : `${m / 60} h`}
              </option>
            ))}
          </select>
        </label>{' '}
        <label>
          {t('backup.keep')}{' '}
          <input
            type="number"
            min={1}
            max={100}
            value={cfg.keep}
            onChange={(e) => {
              const n = Math.round(Number(e.target.value));
              if (n >= 1 && n <= 100) update({ ...cfg, keep: n });
            }}
            style={{ width: '4em' }}
            data-testid="backup-keep"
          />
        </label>
      </div>
      <p>
        <button
          disabled={status !== 'ok'}
          onClick={() => void backup.now().then(refresh)}
          data-testid="backup-now"
        >
          {t('backup.now')}
        </button>{' '}
        <span className="muted small" data-testid="backup-last">
          {backup.last
            ? t('backup.last', {
                time: backup.last.at.toLocaleTimeString(props.locale, { hour: '2-digit', minute: '2-digit' }),
                name: backup.last.name,
              })
            : ''}
        </span>
      </p>
      {backup.error && <p className="error">{backup.error}</p>}
    </fieldset>
  );
}
